import React, { useState, useRef, useCallback, useEffect, useMemo } from 'react';
import { stageAPI } from '../services/api';
import StageModale from './StageModale';

/**
 * SUIVI DE STAGE — Paramétrage › Contacts (lot 3.0)
 * =================================================
 * Spec     : doc/modules/suivi_de_stage.md §6 « Contacts »
 * Maquette : doc/maquettes/suivi_de_stage_maquette.html (#psc-contacts)
 *
 * ── AUCUNE ADRESSE N'EST SAISIE À LA MAIN (correctif du 2026-07-10) ─────────
 * On désigne des UTILISATEURS de l'application ; leur adresse est lue depuis leur
 * compte. Si quelqu'un change d'adresse, la mise à jour est automatique — il n'y
 * a jamais deux vérités pour la même information, ni une adresse à maintenir à
 * deux endroits. C'est pour cela que la liste déroulante ne propose que des
 * comptes, et qu'aucun champ de cet écran n'accepte une adresse libre.
 *
 * ── SIX MODÈLES DE COURRIEL, PAS CINQ (écart E19) ──────────────────────────
 * `stages_contacts` portait cinq couples objet/corps en colonnes. La maquette en
 * dessine six : le sixième — « fiche de liaison validée » — est décrit en prose
 * dans la spec depuis le 2026-07-09 mais n'avait jamais eu de colonne. Ils vivent
 * désormais dans `stages_modeles_mail`, une ligne par type.
 *
 * ── DEUX ÉCARTS DE FORME, ASSUMÉS ──────────────────────────────────────────
 * 1. La maquette ne montre le bloc « Destinataires » que sur TROIS des six
 *    accordéons. Il est ici sur les six : la migration stocke la configuration
 *    pour tous, et un réglage modifiable sur trois modèles et figé sur trois
 *    autres, sans raison visible, se lit comme un défaut.
 * 2. ⚠️ Le bandeau jaune de la maquette renvoie les modes de convention à
 *    « l'étape ③ ». C'est un reliquat de la numérotation à sept étapes : dans
 *    l'éditeur à dix étapes, ils sont à l'étape ⑥ (écart E1). Le texte est
 *    corrigé ici.
 */

/** Variables proposées sous chaque modèle — celles qui ont un sens pour lui. */
const VARIABLES = {
    etudiant_lien:      ['{prenom_etudiant}', '{formation}', '{semestre}', '{date_debut}', '{date_fin}', '{lien}', '{nom_utilisateur}'],
    // 2026-10-09 : le maître (fiche de liaison) dans les modèles qui lui sont adressés.
    maitre_fiche:       ['{prenom_maitre}', '{nom_maitre}', '{prenom_nom_maitre}', '{prenom_nom_etudiant}', '{formation}', '{date_debut}', '{date_fin}', '{lien}', '{nom_utilisateur}'],
    fiche_validee:      ['{prenom_maitre}', '{nom_maitre}', '{prenom_nom_maitre}', '{prenom_nom_etudiant}', '{formation}', '{nom_utilisateur}'],
    convention_depot:   ['{prenom_etudiant}', '{prenom_nom_etudiant}', '{prenom_maitre}', '{nom_maitre}', '{prenom_nom_maitre}', '{formation}', '{date_debut}', '{date_fin}', '{lien}', '{nom_utilisateur}'],
    grille_eval:        ['{prenom_maitre}', '{nom_maitre}', '{prenom_nom_maitre}', '{prenom_nom_etudiant}', '{formation}', '{date_debut}', '{date_fin}', '{lien}', '{nom_utilisateur}'],
    tuteur_affectation: ['{prenom_nom_etudiant}', '{formation}', '{date_debut}', '{date_fin}', '{lien_app}', '{nom_utilisateur}'],
    // ⚠️ Revue de cohérence du 2026-10-06 : le 7ᵉ modèle (E95) n'avait ni variables, ni destinataire, ni icône ici.
    depot_a_corriger:   ['{prenom_etudiant}', '{prenom_nom_etudiant}', '{formation}', '{pieces_a_corriger}', '{lien}', '{nom_utilisateur}'],
    // E137 : le refus du stage.
    stage_refuse:       ['{prenom_etudiant}', '{prenom_nom_etudiant}', '{formation}', '{motif}', '{lien}', '{nom_utilisateur}'],
    // E173 (v85) : ③ la fiche renvoyée à l'étudiant ; ⑤ l'alerte aux responsables (part toute seule).
    fiche_etudiant_a_corriger: ['{prenom_etudiant}', '{prenom_nom_etudiant}', '{formation}', '{commentaire}', '{lien}', '{nom_utilisateur}'],
    fiche_modifiee_maitre:     ['{prenom_nom_etudiant}', '{entreprise}', '{formation}', '{semestre}', '{modifications}', '{lien_app}']
};

/** Le destinataire principal de chaque modèle. Il est FIXE : il découle de l'étape. */
const DESTINATAIRE_PRINCIPAL = {
    etudiant_lien:      'Étudiant',
    maitre_fiche:       'Maître de stage',
    fiche_validee:      'Maître de stage (+ étudiant)',
    convention_depot:   'Étudiant + maître de stage',
    grille_eval:        'Maître de stage',
    tuteur_affectation: 'Tuteur pédagogique',
    depot_a_corriger:   'Étudiant',
    stage_refuse:       'Étudiant',
    fiche_etudiant_a_corriger: 'Étudiant',
    fiche_modifiee_maitre:     'Responsables des stages (envoi automatique)'
};

const ICONES_MAIL = {
    etudiant_lien:      'fa-paper-plane',
    maitre_fiche:       'fa-share',
    fiche_validee:      'fa-circle-check',
    convention_depot:   'fa-file-signature',
    grille_eval:        'fa-star-half-stroke',
    tuteur_affectation: 'fa-chalkboard-user',
    depot_a_corriger:   'fa-file-pen',
    stage_refuse:       'fa-ban',
    fiche_etudiant_a_corriger: 'fa-rotate-left',
    fiche_modifiee_maitre:     'fa-pen'
};

const nomUtilisateur = (u) => u.nom_affiche || `${u.prenom || ''} ${u.nom || ''}`.trim() || u.email;

/** « 13/09 14:22 » — court, parce que la colonne des versions est étroite. */
function dateCourte(iso) {
    if (!iso) return '';
    const d = new Date(iso);
    if (Number.isNaN(d.getTime())) return '';
    return d.toLocaleDateString('fr-FR', { day: '2-digit', month: '2-digit' })
        + ' ' + d.toLocaleTimeString('fr-FR', { hour: '2-digit', minute: '2-digit' });
}

/**
 * Un accordéon de modèle de courriel.
 *
 * ⚠️ Son brouillon est un état LOCAL, initialisé à l'ouverture. Il ne se
 * réinitialise QUE lorsque le modèle change d'identité ou de version enregistrée
 * — jamais à chaque rendu du parent. C'est le défaut du lot 1.3 bis du module
 * Étudiants : une réinitialisation déclenchée par un parent qui se redessine
 * EFFACE LA SAISIE EN COURS, sans erreur et sans que rien ne le signale.
 *
 * ── RÉUTILISÉ PAR LA CERTIFICATION (lot C.0 bis, C33) ──────────────────────
 * « Les mêmes règles que Contacts, telles qu'elles sont codées » : le composant
 * est EXPORTÉ plutôt que recopié — deux copies auraient divergé à la première
 * correction. Ce qui diffère d'un onglet à l'autre arrive en propriétés
 * facultatives ; sans elles, rien ne change pour Stagiaires → Contacts.
 *   variables · destinataire · icone · sousTitre · noteLien · noteDestinataires
 */
export function AccordeonMail({ modele, ouvert, onBasculer, departementId, modifiable, onMiseAJour,
                                variables, destinataire, icone, sousTitre, noteLien, noteDestinataires,
                                // Lot 3.5a-3 : cases de copie propres à un modèle (Cc étudiant, Cc tuteur).
                                ccSupplementaires = [],
                                // Lot 3.5d-2 : cases propres à un modèle, SOUS le corps (« Joindre le fichier Excel »),
                                // rangées dans `destinataires` — écrites seulement cochées, comme les copies ci-dessus.
                                options = [],
                                // Planning oraux (PO.5a) : l'API des modèles — par défaut celle du Stage ; le Planning oraux passe la
                                // sienne (mêmes méthodes, mêmes réponses) pour que ses modèles s'écrivent sous SES droits.
                                api = null,
                                // Planning oraux (PO.5b) : les libellés des copies communes (au Stage : « responsable des stages », « assistantes »).
                                libellesCc = null }) {
    const apiModeles = api || stageAPI;
    const [objet, setObjet] = useState(modele.objet);
    const [corps, setCorps] = useState(modele.corps);
    const [dest, setDest]   = useState(modele.destinataires || {});
    const [commentaire, setCommentaire] = useState('');
    const [enCours, setEnCours] = useState(false);
    const [erreur, setErreur]   = useState(null);
    const [enregistre, setEnregistre] = useState(false);
    const [versions, setVersions] = useState(null);
    const [aRestaurer, setARestaurer] = useState(null);

    const refObjet = useRef(null);
    const refCorps = useRef(null);
    const dernierFocus = useRef('corps');

    /**
     * Le brouillon suit le modèle QUAND CELUI-CI CHANGE EN BASE (enregistrement,
     * rétablissement) — et seulement à ce moment-là.
     *
     * ⚠️⚠️ DÉFAUT TROUVÉ PAR BERTRAND À LA RECETTE, le 2026-09-13, et corrigé ici.
     * Ces dépendances étaient `[modele.id, modele.updated_at]`. Or
     * `listerModelesMail` NE SÉLECTIONNAIT PAS `updated_at` : la valeur était
     * `undefined` à chaque rendu, donc les dépendances ne changeaient JAMAIS,
     * donc l'effet ne rejouait jamais.
     *
     * Conséquence : « Rétablir par défaut » remettait bien le texte d'origine
     * EN BASE — mon banc d'API le vérifiait et il était vert — mais **l'écran
     * continuait d'afficher le texte modifié**. Le bouton se grisait (parce que
     * `modifie` repassait à faux), et rien d'autre ne bougeait. De l'extérieur :
     * « ça ne marche pas ».
     *
     * La leçon, déjà écrite et pourtant reprise en pleine figure : **un banc
     * d'API vert ne prouve pas que l'écran affiche la bonne chose.** Un contrôle
     * à l'image a été ajouté pour cette action précise.
     *
     * ➡️ Les dépendances portent désormais sur **les valeurs du serveur
     * elles-mêmes**, pas sur un horodatage qu'il fallait penser à sélectionner.
     * Elles ne bougent que lorsque le serveur a répondu : taper dans le champ ne
     * change que l'état local, donc **la saisie en cours survit** — c'est le
     * défaut du lot 1.3 bis d'Étudiants, qui ne doit pas revenir par cette porte.
     */
    const referenceServeur = JSON.stringify([modele.objet, modele.corps, modele.destinataires]);
    useEffect(() => {
        setObjet(modele.objet);
        setCorps(modele.corps);
        setDest(modele.destinataires || {});
        setErreur(null);
    }, [modele.id, referenceServeur]); // eslint-disable-line react-hooks/exhaustive-deps

    const modifie = objet !== modele.objet
        || corps !== modele.corps
        || JSON.stringify(dest) !== JSON.stringify(modele.destinataires || {});

    /** Insère une variable AU CURSEUR du dernier champ touché (comportement de la maquette). */
    const insererVariable = useCallback((variable) => {
        const cible = dernierFocus.current === 'objet' ? refObjet.current : refCorps.current;
        if (!cible) return;
        const debut = cible.selectionStart ?? cible.value.length;
        const fin   = cible.selectionEnd   ?? cible.value.length;
        const avant = cible.value.slice(0, debut);
        const apres = cible.value.slice(fin);
        const nouveau = avant + variable + apres;
        if (dernierFocus.current === 'objet') setObjet(nouveau); else setCorps(nouveau);
        // On repose le curseur APRÈS la variable : sans cela il repart au début et
        // la variable suivante s'insère à l'envers.
        requestAnimationFrame(() => {
            cible.focus();
            const p = debut + variable.length;
            cible.setSelectionRange(p, p);
        });
    }, []);

    // L'historique n'est lu QU'À L'OUVERTURE : il n'a aucune raison de voyager
    // avec le reste du paramétrage tant que l'accordéon est replié.
    useEffect(() => {
        if (!ouvert) return;
        let annule = false;
        apiModeles.listerVersionsMail(modele.type_mail, departementId)
            .then((r) => { if (!annule) setVersions(r.data.versions); })
            // ⚠️ L'échec est DIT. Une colonne d'historique muette se lit comme
            // « il n'y a pas de version », ce qui est faux et rassurant.
            .catch(() => { if (!annule) setVersions('erreur'); });
        return () => { annule = true; };
    }, [ouvert, modele.type_mail, departementId, modele.updated_at, apiModeles]);

    const enregistrer = useCallback(async () => {
        setEnCours(true);
        setErreur(null);
        try {
            const r = await apiModeles.enregistrerModeleMail(modele.type_mail, departementId, {
                objet, corps, destinataires: dest, commentaire
            });
            onMiseAJour({ modeles_mail: r.data.modeles_mail });
            setVersions(r.data.versions);
            // Le commentaire appartient à l'enregistrement qui vient d'avoir
            // lieu : le garder ferait passer la même phrase sur la version
            // suivante, qui n'a rien à voir.
            setCommentaire('');
            setEnregistre(true);
            setTimeout(() => setEnregistre(false), 2500);
        } catch (e) {
            setErreur(e.response?.data?.error || "L'enregistrement a échoué");
        } finally {
            setEnCours(false);
        }
    }, [modele.type_mail, departementId, objet, corps, dest, commentaire, onMiseAJour, apiModeles]);

    const restaurer = useCallback(async () => {
        if (!aRestaurer) return;
        setEnCours(true);
        setErreur(null);
        try {
            const r = await apiModeles.restaurerVersionMail(modele.type_mail, aRestaurer.id, departementId);
            onMiseAJour({ modeles_mail: r.data.modeles_mail });
            setVersions(r.data.versions);
            setARestaurer(null);
        } catch (e) {
            setARestaurer(null);
            setErreur(e.response?.data?.error || 'La restauration a échoué');
        } finally {
            setEnCours(false);
        }
    }, [aRestaurer, modele.type_mail, departementId, onMiseAJour, apiModeles]);

    return (
        <div className={'mail-accordion' + (ouvert ? ' open' : '')}>
            <button type="button" className="mail-accordion-header" onClick={onBasculer} aria-expanded={ouvert}>
                <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
                    <i className={'fa-solid ' + (icone || ICONES_MAIL[modele.type_mail] || 'fa-envelope')}
                       style={{ color: 'var(--stg-vert-d)' }} />
                    <div>
                        <div className="mail-titre">{modele.libelle}</div>
                        {/* ⚠️ L'information la plus utile — QUI a touché au texte et
                            QUAND — est lisible SANS ouvrir l'accordéon. Demandé par
                            Bertrand le 2026-09-13 : c'est ce qu'on cherche quand on
                            se demande pourquoi un message a changé. */}
                        <div className="mail-sous">
                            {sousTitre || (modele.etape ? `Étape ${modele.etape}` : 'Sans étape')}
                            {modele.derniere_modif_par
                                ? ` · modifié par ${modele.derniere_modif_par} ${dateCourte(modele.derniere_modif_le)}`
                                : (modele.modifie ? ' · texte personnalisé' : '')}
                        </div>
                    </div>
                </div>
                <i className="fa-solid fa-chevron-down mail-acc-chevron" />
            </button>

            {ouvert && (
                <div className="mail-accordion-body mail-deux-colonnes">
                  <div className="mail-colonne-edition">
                    {erreur && (
                        <div className="stg-encart stg-encart-erreur">
                            <i className="fa-solid fa-circle-exclamation" /><span>{erreur}</span>
                        </div>
                    )}

                    <div className="form-group">
                        <label>Destinataires <span style={{ fontWeight: 400, color: '#64748b' }}>
                            (configuration du courriel — À / Cc / Cci)</span></label>
                        <div className="mail-dest" style={{ marginTop: 4 }}>
                            <span><b>À :</b> {destinataire || DESTINATAIRE_PRINCIPAL[modele.type_mail] || '—'}</span>
                            <label>
                                <input type="checkbox" disabled={!modifiable}
                                       checked={dest.cc_responsables === true}
                                       onChange={(e) => setDest({ ...dest, cc_responsables: e.target.checked })} />
                                {libellesCc?.responsables || 'Cc : responsable des stages'}
                            </label>
                            <label>
                                <input type="checkbox" disabled={!modifiable}
                                       checked={dest.cc_assistantes === true}
                                       onChange={(e) => setDest({ ...dest, cc_assistantes: e.target.checked })} />
                                {libellesCc?.assistantes || 'Cc : assistantes'}
                            </label>
                            {ccSupplementaires.map((c) => (
                                <label key={c.cle}>
                                    <input type="checkbox" disabled={!modifiable}
                                           checked={dest[c.cle] === true}
                                           onChange={(e) => setDest({ ...dest, [c.cle]: e.target.checked })} />
                                    {c.libelle}
                                </label>
                            ))}
                            <label title="Règle RGPD : les destinataires externes sont masqués entre eux">
                                <input type="checkbox" disabled={!modifiable}
                                       checked={dest.cci === true}
                                       onChange={(e) => setDest({ ...dest, cci: e.target.checked })} />
                                Cci (masquer les adresses)
                            </label>
                        </div>
                        {noteDestinataires && (
                            <div style={{ fontSize: 10, color: '#64748b', marginTop: 3 }}>{noteDestinataires}</div>
                        )}
                    </div>

                    <div className="form-group">
                        <label htmlFor={`objet-${modele.type_mail}`}>
                            Objet <span className="form-required">*</span>
                        </label>
                        <input
                            id={`objet-${modele.type_mail}`}
                            ref={refObjet}
                            className="input-sm"
                            value={objet}
                            disabled={!modifiable}
                            onFocus={() => { dernierFocus.current = 'objet'; }}
                            onChange={(e) => setObjet(e.target.value)}
                        />
                    </div>

                    <div className="form-group">
                        <label htmlFor={`corps-${modele.type_mail}`}>
                            Corps du message <span className="form-required">*</span>
                        </label>
                        <textarea
                            id={`corps-${modele.type_mail}`}
                            ref={refCorps}
                            className="input-sm mail-corps"
                            value={corps}
                            disabled={!modifiable}
                            onFocus={() => { dernierFocus.current = 'corps'; }}
                            onChange={(e) => setCorps(e.target.value)}
                        />
                    </div>

                    <div className="mail-vars">
                        <span>Variables :</span>
                        {(variables || VARIABLES[modele.type_mail] || []).map((v) => (
                            <button key={v} type="button" disabled={!modifiable}
                                    title="Insérer au curseur"
                                    onClick={() => insererVariable(v)}>{v}</button>
                        ))}
                    </div>
                    {/* ⚠️ `{lien}` est fabriqué par l'application selon l'étape : il ne
                        se saisit pas à la main, et un lien tapé ici ne mènerait nulle part. */}
                    <div style={{ fontSize: 11, color: '#64748b', marginTop: 6 }}>
                        <i className="fa-solid fa-circle-info" />{' '}
                        {noteLien || (modele.type_mail === 'tuteur_affectation'
                            ? "{lien_app} est l'adresse de l'application, posée dans Administration."
                            : "{lien} est le lien tokenisé fabriqué par l'application : ne le saisissez pas à la main.")}
                    </div>
                    {options.map((o) => (
                        <label key={o.cle} className="mail-option" style={{ display: 'flex', gap: 6, alignItems: 'center', fontSize: 12, marginTop: 8 }}>
                            <input type="checkbox" disabled={!modifiable} checked={dest[o.cle] === true}
                                   onChange={(e) => {
                                       const d = { ...dest };
                                       if (e.target.checked) d[o.cle] = true; else delete d[o.cle];
                                       setDest(d);
                                   }} />
                            {o.libelle}
                            {o.aide && <span style={{ color: '#64748b', fontSize: 11 }}>{o.aide}</span>}
                        </label>
                    ))}

                    {/* Le commentaire est JUSTE AU-DESSUS du bouton : c'est là qu'on
                        l'écrit, au moment où l'on sait encore pourquoi. */}
                    <div className="form-group" style={{ marginTop: 12 }}>
                        <label htmlFor={`comm-${modele.type_mail}`}>
                            Commentaire <span style={{ fontWeight: 400, color: '#64748b' }}>
                                (facultatif — dit POURQUOI ce changement)</span>
                        </label>
                        <input
                            id={`comm-${modele.type_mail}`}
                            className="input-sm"
                            maxLength={300}
                            placeholder="Ex : ajout du délai de retour, demandé par la scolarité"
                            value={commentaire}
                            disabled={!modifiable}
                            onChange={(e) => setCommentaire(e.target.value)}
                        />
                    </div>

                    <div className="mail-pied">
                        <span style={{ fontSize: 11, color: '#64748b' }}>
                            {modifie
                                ? <><i className="fa-solid fa-pen" /> Modifications non enregistrées</>
                                : <>&nbsp;</>}
                        </span>
                        <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
                            {enregistre && (
                                <span style={{ fontSize: 11, color: '#15803d', fontWeight: 600 }}>
                                    <i className="fa-solid fa-check" /> Enregistré
                                </span>
                            )}
                            <button
                                type="button" className="btn btn-green btn-sm"
                                disabled={!modifiable || enCours || !modifie}
                                onClick={enregistrer}
                            >
                                {enCours
                                    ? <><i className="fa-solid fa-circle-notch stg-tourne" /> …</>
                                    : <><i className="fa-solid fa-check" /> Enregistrer</>}
                            </button>
                        </div>
                    </div>
                  </div>

                  {/* ── LA COLONNE DES VERSIONS ────────────────────────────── */}
                  <div className="mail-colonne-versions">
                    <div className="mail-versions-titre">Versions</div>

                    {versions === null && (
                        <div className="mail-version-vide">
                            <i className="fa-solid fa-circle-notch stg-tourne" /> Lecture…
                        </div>
                    )}
                    {versions === 'erreur' && (
                        <div className="stg-encart stg-encart-erreur" style={{ marginBottom: 0 }}>
                            <i className="fa-solid fa-circle-exclamation" />
                            <span>Historique illisible — réessayez en rouvrant ce message.</span>
                        </div>
                    )}

                    {Array.isArray(versions) && versions.map((v, i) => (
                        <div key={v.id} className={'mail-version' + (i === 0 ? ' actuelle' : '')}>
                            <div className="mail-version-entete">
                                <i className={'fa-solid ' + (i === 0 ? 'fa-circle-dot' : 'fa-circle')} />
                                <span className="mail-version-qui">
                                    {i === 0 && <b>ACTUELLE — </b>}
                                    {v.origine
                                        ? "Version d'origine"
                                        : `${v.cree_par_nom || 'Auteur inconnu'} · ${dateCourte(v.cree_le)}`}
                                </span>
                            </div>
                            {v.commentaire && (
                                <div className="mail-version-comm">
                                    {v.restauration && <i className="fa-solid fa-rotate-left" />}{' '}
                                    « {v.commentaire} »
                                </div>
                            )}
                            {/* Restaurer la version courante n'aurait aucun effet :
                                le bouton n'apparaît pas, plutôt que d'apparaître grisé
                                sans que l'on comprenne pourquoi. */}
                            {i > 0 && (
                                <button
                                    type="button" className="btn btn-outline btn-sm mail-version-btn"
                                    disabled={!modifiable || enCours}
                                    onClick={() => setARestaurer(v)}
                                >
                                    <i className="fa-solid fa-rotate-left" /> Restaurer
                                </button>
                            )}
                        </div>
                    ))}
                  </div>
                </div>
            )}

            {/* Récapitulatif AVANT la restauration (règle 6). */}
            {aRestaurer && (
                <StageModale
                    titre="Restaurer cette version ?"
                    icone="fa-rotate-left"
                    enCours={enCours}
                    libelleValider="Restaurer"
                    onValider={restaurer}
                    onFermer={() => setARestaurer(null)}
                >
                    <div><b>{modele.libelle}</b></div>
                    <ul>
                        <li>
                            Version {aRestaurer.origine
                                ? "d'origine, livrée avec l'application"
                                : `de ${aRestaurer.cree_par_nom || 'auteur inconnu'}, ${dateCourte(aRestaurer.cree_le)}`}
                        </li>
                        <li>L'objet, le corps <b>et les destinataires</b> reprennent cette version.</li>
                        <li>
                            ⚠️ <b>Rien n'est effacé</b> : la restauration s'ajoute à l'historique.
                            Le texte actuel restera consultable et restaurable.
                        </li>
                    </ul>
                    <div style={{ marginTop: 10, padding: '8px 10px', background: '#f8fafc',
                                  border: '1px solid #e2e8f0', borderRadius: 6, fontSize: 12 }}>
                        <div style={{ color: '#64748b', fontSize: 11, marginBottom: 4 }}>Objet restauré :</div>
                        {aRestaurer.objet}
                    </div>
                </StageModale>
            )}
        </div>
    );
}

/**
 * L'ÉQUIPE DU STAGE — assistantes et responsables des stages (déplacé le 2026-10-09 dans
 * « Paramétrage général → Équipe », écart E153 : ces rôles servent à TOUT le module — copies des
 * courriels stagiaires et tuteurs, refus d'un stage (E67), récapitulatif des notifications —, ils
 * n'avaient rien à faire sous « Stagiaires »). Même écriture qu'avant : la route rend les contacts
 * à jour, `onContacts` les pose sans relire le serveur.
 */
export function EquipeStage({ contacts: contactsRecus, departementId, modifiable, onContacts }) {
    const [enCours, setEnCours] = useState(false);
    const [erreur, setErreur]   = useState(null);
    const [avertissement, setAvertissement] = useState(null);

    const contacts = contactsRecus || { assistantes: [], responsables: [], utilisateurs_disponibles: [] };

    const ecrire = useCallback(async (assistantes, responsables) => {
        setEnCours(true);
        setErreur(null);
        setAvertissement(null);
        try {
            const r = await stageAPI.enregistrerContacts(departementId, {
                assistantes_user_ids: assistantes,
                responsables_user_ids: responsables
            });
            onContacts(r.data.contacts);
            // ⚠️ Un identifiant écarté par le serveur est DIT. L'ignorer en silence
            // laisserait croire que le réglage est en place.
            if (r.data.refuses?.length) {
                setAvertissement(`${r.data.refuses.length} utilisateur(s) écarté(s) : ils n'appartiennent pas à ce département.`);
            }
        } catch (e) {
            setErreur(e.response?.data?.error || "L'enregistrement des contacts a échoué");
        } finally {
            setEnCours(false);
        }
    }, [departementId, onContacts]);

    const ids = (liste) => liste.map((u) => u.id);

    const ajouter = useCallback((role, userId) => {
        const id = Number(userId);
        if (!id) return;
        const a = ids(contacts.assistantes);
        const r = ids(contacts.responsables);
        if (role === 'assistantes') { if (!a.includes(id)) ecrire([...a, id], r); }
        else                        { if (!r.includes(id)) ecrire(a, [...r, id]); }
    }, [contacts, ecrire]);

    const retirer = useCallback((role, userId) => {
        const a = ids(contacts.assistantes);
        const r = ids(contacts.responsables);
        if (role === 'assistantes') ecrire(a.filter((x) => x !== userId), r);
        else                        ecrire(a, r.filter((x) => x !== userId));
    }, [contacts, ecrire]);

    const colonne = (role, titre, aide, liste) => {
        const dejaPris = new Set(ids(liste));
        const disponibles = (contacts.utilisateurs_disponibles || []).filter((u) => !dejaPris.has(u.id));
        return (
            <div>
                <label style={{ fontSize: 11, fontWeight: 600, display: 'block', marginBottom: 2 }}>{titre}</label>
                <div style={{ fontSize: 11, color: '#64748b', marginBottom: 5 }}>{aide}</div>
                <div className="ctc-chips">
                    {liste.length === 0 && <div className="ctc-vide">Personne n'est désigné.</div>}
                    {liste.map((u) => (
                        <div className="ctc-chip" key={u.id}>
                            <span>
                                <b>{nomUtilisateur(u)}</b>
                                <span style={{ color: '#64748b' }}> · {u.email}</span>
                            </span>
                            <button
                                type="button" aria-label={`Retirer ${nomUtilisateur(u)}`}
                                title="Retirer" disabled={!modifiable || enCours}
                                onClick={() => retirer(role, u.id)}
                            >
                                <i className="fa-solid fa-xmark" />
                            </button>
                        </div>
                    ))}
                </div>
                <select
                    className="input-sm" style={{ marginTop: 6 }}
                    value=""
                    disabled={!modifiable || enCours || !disponibles.length}
                    onChange={(e) => { ajouter(role, e.target.value); e.target.value = ''; }}
                    aria-label={`Ajouter un utilisateur — ${titre}`}
                >
                    <option value="">
                        {disponibles.length ? '+ Ajouter un utilisateur…' : 'Aucun autre utilisateur du département'}
                    </option>
                    {disponibles.map((u) => (
                        <option key={u.id} value={u.id}>{nomUtilisateur(u)} — {u.email}</option>
                    ))}
                </select>
            </div>
        );
    };

    return (
        <div className="param-card">
            <div className="param-card-header">
                <span><i className="fa-solid fa-users" /> Équipe du stage</span>
                <span style={{ fontSize: 11, color: '#64748b', fontWeight: 400 }}>Réglage du département, pour tout le module</span>
            </div>
            <div className="param-card-body">
                <div className="stg-encart stg-encart-info">
                    <i className="fa-solid fa-circle-info" />
                    <span>
                        On désigne ici des <b>utilisateurs de l'application</b> (créés dans{' '}
                        <b>Administration → Gestion des utilisateurs</b>). Leur{' '}
                        <b>adresse est récupérée automatiquement</b> — aucune saisie manuelle.
                        Ces rôles servent à <b>tout le module</b> : copies des courriels (stagiaires, tuteurs),
                        refus d'un stage, récapitulatif des notifications.
                    </span>
                </div>
                {!modifiable && (
                    <div className="stg-encart stg-encart-alerte">
                        <i className="fa-solid fa-lock" /><span>Lecture seule : votre profil ne permet pas de modifier ce module.</span>
                    </div>
                )}
                {erreur && (
                    <div className="stg-encart stg-encart-erreur">
                        <i className="fa-solid fa-circle-exclamation" /><span>{erreur}</span>
                    </div>
                )}
                {avertissement && (
                    <div className="stg-encart stg-encart-alerte">
                        <i className="fa-solid fa-triangle-exclamation" /><span>{avertissement}</span>
                    </div>
                )}
                <div className="ctc-grille">
                    {colonne('assistantes', 'Assistantes (secrétariat)', 'En copie des courriels quand le modèle coche « Cc assistantes ».', contacts.assistantes)}
                    {colonne('responsables', 'Responsable(s) des stages', 'En copie quand le modèle coche « Cc responsable » ; peuvent refuser un stage.', contacts.responsables)}
                </div>
            </div>
        </div>
    );
}

/**
 * Stagiaires → Paramétrage → COURRIELS (anciennement « Contacts ») : les modèles de courriel des
 * stagiaires, seuls. L'équipe (assistantes, responsables) est dans « Paramétrage général → Équipe ».
 */
function StageParametrageContacts({ donnees, departementId, modifiable, onMiseAJour, onEquipe, configurationId = null }) {
    const [ouvert, setOuvert] = useState(null);
    const modelesMail = donnees.modeles_mail || [];
    // L2a (E170, v87) : dans une vignette, chaque courriel est celui de CE processus — les trois appels le portent.
    const apiProcessus = useMemo(() => (configurationId ? {
        enregistrerModeleMail: (t, d, data) => stageAPI.enregistrerModeleMail(t, d, data, configurationId),
        listerVersionsMail: (t, d) => stageAPI.listerVersionsMail(t, d, configurationId),
        restaurerVersionMail: (t, v, d) => stageAPI.restaurerVersionMail(t, v, d, configurationId)
    } : null), [configurationId]);

    return (
        // ⚠️ PLEINE LARGEUR depuis le lot 3.0 bis, à la demande de Bertrand : la
        // carte était plafonnée à 760 px alors que la page en fait ~1400 —
        // mesuré. La place perdue était déjà gênante ; avec la colonne des
        // versions, elle devenait bloquante.
        <div className="param-card">
            <div className="param-card-header">
                <span><i className="fa-solid fa-envelope" /> Courriels des stagiaires</span>
            </div>
            <div className="param-card-body">
                <div className="stg-encart stg-encart-info">
                    <i className="fa-solid fa-users" />
                    <span>
                        Les <b>assistantes</b> et les <b>responsables des stages</b> mis en copie se désignent dans{' '}
                        {onEquipe
                            ? <button type="button" className="stg-lien-bouton" onClick={onEquipe}>Paramétrage général → Équipe</button>
                            : <b>Paramétrage général → Équipe</b>}.
                    </span>
                </div>

                {/* ⚠️ « étape ⑥ », pas « étape ③ » : voir l'en-tête de ce fichier. */}
                <div className="stg-encart stg-encart-alerte">
                    <i className="fa-solid fa-circle-info" />
                    <span>
                        Les textes et modes de convention (retrait, postal, numérique) se configurent
                        par formation dans <b>Paramétrage → Processus → étape ⑦</b>.
                    </span>
                </div>

                <div style={{ fontSize: 11, fontWeight: 700, color: '#64748b', textTransform: 'uppercase',
                              letterSpacing: '.5px', marginBottom: 10 }}>
                    Modèles de courriel (objet + corps)
                </div>

                {modelesMail.map((m) => (
                    <AccordeonMail
                        key={m.type_mail}
                        modele={m}
                        ouvert={ouvert === m.type_mail}
                        onBasculer={() => setOuvert(ouvert === m.type_mail ? null : m.type_mail)}
                        departementId={departementId}
                        modifiable={modifiable}
                        onMiseAJour={onMiseAJour}
                        api={apiProcessus}
                    />
                ))}
            </div>
        </div>
    );
}

export default StageParametrageContacts;
